/* Filters, sort and pagination without a page reload.
   <facet-filters data-section-id="..."> wraps everything that changes (toolbar, filter forms, results, pagination).
   - A change in any <form data-facet-form> or a sort select ([data-facet-sort]) builds the new address,
     fetches the section from the server (Section Rendering API) and swaps every [data-facet-part="name"] block.
   - Links marked data-facet-link (active filter pills, pagination, "clear all") do the same.
   - The address bar follows (pushState / popstate). If anything fails the browser simply opens the address.
   <cs-price-slider> keeps the two sliders and the two number fields of a price filter in step. */

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.status = this.querySelector('[data-facet-status]');
    this.onChange = (event) => this.handleChange(event);
    this.onSubmit = (event) => this.handleSubmit(event);
    this.onClick = (event) => this.handleClick(event);
    this.onPop = () => this.load(location.href, { push: false });
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPop);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPop);
    this.controller?.abort();
  }

  handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.matches('input[type="range"]')) return; // sliders report through their number fields
    if (target.matches('[data-facet-sort]')) {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      this.load(url.toString());
      return;
    }
    const form = target.closest('form[data-facet-form]');
    if (form) this.load(this.formUrl(form));
  }

  handleSubmit(event) {
    const form = event.target.closest?.('form[data-facet-form]');
    if (!form) return;
    event.preventDefault();
    this.load(this.formUrl(form));
  }

  handleClick(event) {
    const link = event.target.closest('a[data-facet-link]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
    event.preventDefault();
    this.load(link.href, { scroll: link.closest('.pagination') !== null });
  }

  formUrl(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value === '' || value === null) continue;
      if (key === 'sort_by') params.set(key, value);
      else params.append(key, value);
    }
    params.delete('page');
    const url = new URL(form.getAttribute('action') || location.pathname, location.href);
    url.search = params.toString();
    return url.toString();
  }

  async load(address, { push = true, scroll = false } = {}) {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.setBusy(true);
    try {
      const request = new URL(address, location.href);
      request.searchParams.set('section_id', this.sectionId);
      const response = await fetch(request, { signal });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.swap(doc);
      if (push) history.pushState({ facets: true }, '', address);
      if (scroll) this.scrollToResults();
      if (this.status) this.status.textContent = this.dataset.updatedText || '';
      document.dispatchEvent(new CustomEvent('facets:updated', { detail: { url: address } }));
      if (window.Theme?.initAnimations) window.Theme.initAnimations(this);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = address; // fall back to a normal page load
    } finally {
      if (!signal.aborted) this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.classList.toggle('is-loading', busy);
    this.setAttribute('aria-busy', String(busy));
  }

  scrollToResults() {
    const results = this.querySelector('[data-facet-part="results"]') || this;
    results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  swap(doc) {
    for (const next of doc.querySelectorAll('[data-facet-part]')) {
      const name = next.dataset.facetPart;
      const current = this.querySelector(`[data-facet-part="${name}"]`);
      if (!current) continue;

      // keep which groups the shopper opened or closed, the focused control and the scroll position of a drawer
      const groups = new Map([...current.querySelectorAll('details[data-facet-group]')].map((d) => [d.dataset.facetGroup, d.open]));
      const focus = current.contains(document.activeElement) ? this.describe(document.activeElement) : null;
      const scroller = current.closest('.drawer__body');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      current.innerHTML = next.innerHTML;

      current.querySelectorAll('details[data-facet-group]').forEach((d) => {
        if (groups.has(d.dataset.facetGroup)) d.open = groups.get(d.dataset.facetGroup);
      });
      if (scroller) scroller.scrollTop = scrollTop;
      if (focus) this.restore(current, focus);
    }
  }

  describe(element) {
    if (element.id) return { selector: `#${CSS.escape(element.id)}` };
    if (element.name) {
      const value = element.type === 'checkbox' || element.type === 'radio' ? `[value="${CSS.escape(element.value)}"]` : '';
      return { selector: `[name="${CSS.escape(element.name)}"]${value}` };
    }
    return null;
  }

  restore(root, focus) {
    const element = focus && root.querySelector(focus.selector);
    if (element && typeof element.focus === 'function') element.focus({ preventScroll: true });
  }
}
if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);

/* Two-handle price slider with two number fields */
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.max = Number(this.dataset.max) || 0;
    this.track = this.querySelector('.facets__range');
    this.sliderMin = this.querySelector('[data-range-min]');
    this.sliderMax = this.querySelector('[data-range-max]');
    this.fieldMin = this.querySelector('[data-price-min]');
    this.fieldMax = this.querySelector('[data-price-max]');
    if (!this.sliderMin || !this.sliderMax || !this.fieldMin || !this.fieldMax) return;

    this.sliderMin.addEventListener('input', () => this.slide('min'));
    this.sliderMax.addEventListener('input', () => this.slide('max'));
    this.sliderMin.addEventListener('change', () => this.commit('min'));
    this.sliderMax.addEventListener('change', () => this.commit('max'));
    this.fieldMin.addEventListener('input', () => this.type());
    this.fieldMax.addEventListener('input', () => this.type());
    this.fieldMin.addEventListener('change', () => this.type());
    this.fieldMax.addEventListener('change', () => this.type());
    this.paint();
  }

  slide(side) {
    const low = Number(this.sliderMin.value);
    const high = Number(this.sliderMax.value);
    if (low > high) {
      if (side === 'min') this.sliderMin.value = high;
      else this.sliderMax.value = low;
    }
    this.fieldMin.value = Number(this.sliderMin.value) <= 0 ? '' : this.sliderMin.value;
    this.fieldMax.value = Number(this.sliderMax.value) >= this.max ? '' : this.sliderMax.value;
    this.paint();
  }

  commit(side) {
    this.slide(side);
    (side === 'min' ? this.fieldMin : this.fieldMax).dispatchEvent(new Event('change', { bubbles: true }));
  }

  type() {
    const low = this.fieldMin.value === '' ? 0 : Math.max(0, Number(this.fieldMin.value));
    const high = this.fieldMax.value === '' ? this.max : Math.min(this.max, Number(this.fieldMax.value));
    this.sliderMin.value = Math.min(low, high);
    this.sliderMax.value = Math.max(low, high);
    this.paint();
  }

  paint() {
    if (!this.track || !this.max) return;
    this.track.style.setProperty('--range-min', `${(Number(this.sliderMin.value) / this.max) * 100}%`);
    this.track.style.setProperty('--range-max', `${(Number(this.sliderMax.value) / this.max) * 100}%`);
  }
}
if (!customElements.get('cs-price-slider')) customElements.define('cs-price-slider', PriceRange);

/* Sort menu: a button that opens a small list of sort options under it (live look). Picking an option writes it into
   the hidden [data-facet-sort] field and fires "change", which <facet-filters> turns into a new results load. */
class SortMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sort-toggle]');
    this.panel = this.querySelector('[data-sort-popover]');
    this.field = this.querySelector('[data-facet-sort]');
    if (!this.button || !this.panel) return;
    this.onToggle = () => this.setOpen(this.panel.hidden);
    this.onPick = (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.pick(option);
    };
    this.onKey = (event) => this.handleKey(event);
    this.onOutside = (event) => { if (!this.contains(event.target)) this.setOpen(false); };
    this.button.addEventListener('click', this.onToggle);
    this.panel.addEventListener('click', this.onPick);
    this.addEventListener('keydown', this.onKey);
    document.addEventListener('pointerdown', this.onOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutside);
  }

  get options() { return [...this.panel.querySelectorAll('[role="option"]')]; }

  setOpen(open, focusOption = false) {
    this.panel.hidden = !open;
    this.button.setAttribute('aria-expanded', String(open));
    if (open && focusOption) (this.options.find((o) => o.getAttribute('aria-selected') === 'true') || this.options[0])?.focus();
  }

  pick(option) {
    this.options.forEach((o) => o.setAttribute('aria-selected', String(o === option)));
    const current = this.querySelector('.facets__sort-current');
    if (current) current.textContent = option.textContent.trim();
    this.setOpen(false);
    this.button.focus();
    if (this.field && this.field.value !== option.value) {
      this.field.value = option.value;
      this.field.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  handleKey(event) {
    const open = !this.panel.hidden;
    if (event.key === 'Escape' && open) { event.preventDefault(); this.setOpen(false); this.button.focus(); return; }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    if (!open) { this.setOpen(true, true); return; }
    const list = this.options;
    const index = list.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? Math.min(list.length - 1, index + 1) : Math.max(0, index - 1);
    list[next]?.focus();
  }
}
if (!customElements.get('cs-sort-menu')) customElements.define('cs-sort-menu', SortMenu);
